import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {DeviceService} from '../../../../../services/device.service';
import {LogService} from '../../../../../services/log.service';
import {ToggleSwitchComponent} from '../../../../../shared/components/button-toggle/button-toggle';
import {InputSelectComponent} from '../../../../../shared/components/input-select/input-select';
import {InputSubmitComponent} from '../../../../../shared/components/input-submit/input-submit.component';
import {InputTextComponent} from '../../../../../shared/components/input-text/input-text.component';
import {InputTextarea} from '../../../../../shared/components/input-textarea/input-textarea';
import {TranslateSafePipe} from '../../../../../shared/pipes/translation.pipe';
import {AlarmRuleActionDto, AlarmRuleActionType} from '../../../../../types/dtos/alarm-rule-action.dto';
import {AlarmRuleConditionDto, AlarmRuleConditionType} from '../../../../../types/dtos/alarm-rule-condition.dto';
import {AlarmRuleTriggerType} from '../../../../../types/dtos/alarm-rule-trigger.dto';
import {
  AlarmRuleActionFormDto,
  AlarmRuleAddOrUpdateFormDto,
  AlarmRuleConditionFormDto,
  AlarmRuleDeviceDto,
  AlarmRuleDto,
  DeleteDeviceAlarmRuleOverrideAlarmActionEntity,
  DeleteDeviceAlarmRuleOverrideAlarmConditionEntity,
  DeviceAlarmRuleOverrideAlarmActionEntity,
  DeviceAlarmRuleOverrideAlarmConditionEntity,
  UpdateAlarmRuleDeviceDto,
} from '../../../../../types/dtos/alarm-rule.dto';
import {BaseResponseDto} from '../../../../../types/dtos/base.dto';
import {AlarmSeverity, AlarmType} from '../../../../../types/enums/alarm.enum';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../../../types/enums/logging.enum';
import {
  InputSelectInterface,
  InputSubmitInterface,
  InputTextAreaInterface,
  InputTextInterface,
  InputToggleInterface,
} from '../../../../../types/interfaces/input.interface';
import {
  AlarmRuleActions,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/alarm-rule-actions';
import {
  AlarmResolvedRuleAction,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/rule-actions/alarm-resolved/alarm-resolved.rule-action';
import {
  BaseRuleAction,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/rule-actions/base-rule-action';
import {
  CreateAlarmRuleAction,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/rule-actions/create-alarm-action/create-alarm.rule-action';
import {
  DeviceUpdateRuleAction,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/rule-actions/device-update-action/device-update.rule-action';
import {
  EmailRuleAction,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-actions/rule-actions/email-action/email.rule-action';
import {
  AlarmRuleConditions,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-conditions/alarm-rule-conditions';
import {
  BaseRuleCondition,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-conditions/rule-conditions/base-rule-condition';
import {
  ComparisonRuleCondition,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-conditions/rule-conditions/comparison-condition/comparison.rule-condition';
import {
  HasAlarmCheckRuleCondition,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-conditions/rule-conditions/has-alarm-check/has-alarm-check.rule-condition';
import {
  LastActivityRuleCondition,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-conditions/rule-conditions/last-activity-condition/last-activity.rule-condition';
import {
  AlarmRuleTriggers,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-triggers/alarm-rule-triggers';
import {
  BaseRuleTrigger,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-triggers/rule-triggers/base-rule-trigger';
import {
  CronRuleTrigger,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-triggers/rule-triggers/cron-trigger/cron.rule-trigger';
import {
  DeviceInputRuleTrigger,
} from '../../../../settings/settings-alarm-rules/alarm-rules-dialog/alarm-rule-triggers/rule-triggers/device-input-trigger/device-input.rule-trigger';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-device-edit-alarm-rules-edit',
  imports: [
    InputTextComponent,
    ToggleSwitchComponent,
    InputTextarea,
    ReactiveFormsModule,
    InputSelectComponent,
    InputSubmitComponent,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    AlarmRuleTriggers,
    AlarmRuleConditions,
    AlarmRuleActions,
    TranslateSafePipe,
  ],
  templateUrl: './device-edit-alarm-rules-edit.html',
  styleUrl: './device-edit-alarm-rules-edit.css',
})
export class DeviceEditAlarmRulesEdit implements OnInit {
  @Input() mode: 'override' | 'view' = 'view';
  public loading: boolean = true;
  public alarmRule!: AlarmRuleDto;
  public formGroup: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    isActive: new FormControl(true),
    description: new FormControl(''),
    severity: new FormControl('info', Validators.required),
    alarmType: new FormControl('', Validators.required),
    cooldown: new FormControl(3600, Validators.required),
    triggers: new FormArray<FormGroup>([]),
    conditions: new FormArray<FormGroup>([]),
    actions: new FormArray<FormGroup>([]),
  });
  public nameInput: InputTextInterface = {
    formGroup: this.formGroup,
    id: 'name',
    isTranslated: true,
    label: 'common.input.alarm-rule-name.label',
    type: 'text',
    placeholder: 'common.input.alarm-rule-name.placeholder',
  };
  public isActiveToggle: InputToggleInterface = {
    formGroup: this.formGroup,
    id: 'isActive',
    isTranslated: true,
    readonly: true,
    label: 'common.input.alarm-rule-active.label',
  };
  public descriptionInput: InputTextAreaInterface = {
    formGroup: this.formGroup,
    id: 'description',
    isTranslated: true,
    label: 'common.input.description.label',
    placeholder: 'common.input.description.placeholder',
  };
  public severitySelect: InputSelectInterface = {
    id: 'severity',
    formGroup: this.formGroup,
    isTranslated: true,
    label: 'common.table.severity.label',
    options: [
      {
        isTranslated: true,
        label: 'common.enum.alarm-severity.info',
        value: AlarmSeverity.INFO,
      },
      {
        isTranslated: true,
        label: 'common.enum.alarm-severity.warning',
        value: AlarmSeverity.WARNING,
      },
      {
        isTranslated: true,
        label: 'common.enum.alarm-severity.high',
        value: AlarmSeverity.HIGH,
      },
      {
        isTranslated: true,
        label: 'common.enum.alarm-severity.critical',
        value: AlarmSeverity.CRITICAL,
      },
    ],
  };
  public alarmTypeInput: InputSelectInterface = {
    id: 'alarmType',
    formGroup: this.formGroup,
    isTranslated: true,
    label: 'common.table.alarm-type.label',
    options: [],
  };
  /*public cooldownInput: InputTextInterface = {
   id: 'cooldown',
   formGroup: this.formGroup,
   label: 'Cooldown (Sekunden)',
   type: 'number',
   required: true,
   };*/
  public submitButton: InputSubmitInterface = {
    formGroup: this.formGroup,
    isTranslated: true,
    label: 'common.actions.save',
    iconName: 'save',
  };
  public readonly triggerDefinitions: Record<AlarmRuleTriggerType, BaseRuleTrigger> = {
    [AlarmRuleTriggerType.CRON]: new CronRuleTrigger(),
    [AlarmRuleTriggerType.DEVICE_INPUT]: new DeviceInputRuleTrigger(),
  };
  public readonly conditionDefinitions: Record<AlarmRuleConditionType, BaseRuleCondition> = {
    [AlarmRuleConditionType.LAST_ACTIVITY]: new LastActivityRuleCondition(),
    [AlarmRuleConditionType.HAS_ALARM_CHECK]: new HasAlarmCheckRuleCondition(),
    [AlarmRuleConditionType.COMPARISON]: new ComparisonRuleCondition(),
  };
  public readonly actionDefinitions: Record<AlarmRuleActionType, BaseRuleAction> = {
    [AlarmRuleActionType.CREATE_ALARM]: new CreateAlarmRuleAction(),
    [AlarmRuleActionType.DEVICE_UPDATE]: new DeviceUpdateRuleAction(),
    [AlarmRuleActionType.EMAIL]: new EmailRuleAction(),
    // [AlarmRuleActionType.WEBHOOK]: new WebhookRuleAction(),
    [AlarmRuleActionType.ALARM_RESOLVED]: new AlarmResolvedRuleAction(),
  };

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _logService: LogService,
    private readonly _messageService: MessageService,
    private readonly _translateService: TranslateService,
  ) {
  }

  private _deviceId!: number;

  @Input()
  set deviceId(deviceId: number | null) {
    if (!deviceId) return;
    this._deviceId = deviceId!;

    this._fetchDeviceAlarmRule();
  }

  private _alarmRuleId!: number;

  @Input()
  set alarmRuleId(alarmRuleId: number | null) {
    if (!alarmRuleId) return;
    this._alarmRuleId = alarmRuleId!;

    this._fetchDeviceAlarmRule();
  }

  public get triggersFormGroup(): FormArray<FormGroup> {
    return this.formGroup.get('triggers') as FormArray<FormGroup>;
  }

  public get conditionsFormGroup(): FormArray<FormGroup> {
    return this.formGroup.get('conditions') as FormArray<FormGroup>;
  }

  public get actionsFormGroup(): FormArray<FormGroup> {
    return this.formGroup.get('actions') as FormArray<FormGroup>;
  }

  public ngOnInit(): void {
    this.alarmTypeInput.options = Object.values(AlarmType)
      .map((key) => ({
        isTranslated: true,
        label: 'common.enum.alarm-type.' + key,
        value: key,
      }));
  }

  public override(): void {
    if (this.mode !== 'override') return;
    if (!this.alarmRule) return;

    this.loading = true;

    const values: AlarmRuleAddOrUpdateFormDto = this.formGroup.value as AlarmRuleAddOrUpdateFormDto;

    let overrideActions: DeviceAlarmRuleOverrideAlarmActionEntity[] = this._findOverrideAlarmRuleActionDto(
      this.alarmRule,
      values.actions,
    );
    let overrideConditions: DeviceAlarmRuleOverrideAlarmConditionEntity[] = this._findOverrideAlarmRuleConditionDto(
      this.alarmRule,
      values.conditions,
    );

    let alarmRule: UpdateAlarmRuleDeviceDto = {
      overrideActions,
      overrideConditions,
      deleteOverrideActions: this._findDeletingActions(this.alarmRule, overrideActions),
      deleteOverrideConditions: this._findDeletingConditions(this.alarmRule, overrideConditions),
    };

    if (this._alarmRuleId && this._deviceId) {
      this._deviceService.updateDeviceAlarmRule(this._deviceId, this._alarmRuleId, alarmRule)
        .subscribe({
          next: (result: BaseResponseDto<AlarmRuleDto>) => {
            if (result.data) {
              this.alarmRule = result.data;

              this._fillForm();
            }
            this._messageService.add({
              severity: 'success',
              summary: this._translateService.instant('common.messages.success.summary'),
              detail: this._translateService.instant('common.messages.success.edit-device-alarm-rule'),
            });

            this._logService.sendLog({
              action: LoggingActionEnum.UPDATE,
              target: LoggingTargetEnum.ALARM,
              description: this._translateService.instant('common.messages.success.edit-device-alarm-rule'),
            });
          },
          error: (error) => {
            this._messageService.add({
              severity: 'error',
              summary: this._translateService.instant('common.title.error'),
              detail: this._translateService.instant('common.messages.error.edit-device-alarm-rule'),
            });

            this._logService.sendLog({
              action: LoggingActionEnum.UPDATE,
              target: LoggingTargetEnum.ALARM,
              description: this._translateService.instant('common.messages.error.edit-device-alarm-rule'),
            });
          },
          complete: () => {
            this.loading = false;
          },
        });
    }
  }

  private _findDeletingConditions(
    alarmRule: AlarmRuleDto,
    overrideConditions: DeviceAlarmRuleOverrideAlarmConditionEntity[],
  ): DeleteDeviceAlarmRuleOverrideAlarmConditionEntity[] {
    let deleteAlarmOverride: DeleteDeviceAlarmRuleOverrideAlarmConditionEntity[] = [];

    let deviceOverrides = alarmRule.devices
      ?.find((device) => device.device_id === this._deviceId);

    if (!deviceOverrides) return deleteAlarmOverride;

    deviceOverrides.overrideConditions?.forEach((overrideCondition) => {
      let foundCondition = overrideConditions
        .find((condition) => condition.alarm_rule_condition_id === overrideCondition.alarm_rule_condition_id);

      if (!foundCondition) {
        deleteAlarmOverride.push({
          alarm_rule_condition_id: overrideCondition.alarm_rule_condition_id,
          device_id: this._deviceId,
          alarm_rule_id: this._alarmRuleId,
        });
      }
    });

    return deleteAlarmOverride;
  }

  private _findDeletingActions(
    alarmRule: AlarmRuleDto,
    overrideActions: DeviceAlarmRuleOverrideAlarmActionEntity[],
  ): DeleteDeviceAlarmRuleOverrideAlarmActionEntity[] {
    let deleteAlarmOverride: DeleteDeviceAlarmRuleOverrideAlarmActionEntity[] = [];

    let deviceOverrides = alarmRule.devices
      ?.find((device) => device.device_id === this._deviceId);

    if (!deviceOverrides) return deleteAlarmOverride;

    deviceOverrides.overrideActions?.forEach((overrideAction) => {
      let foundCondition = overrideActions
        .find((action) => action.alarm_rule_action_id === overrideAction.alarm_rule_action_id);

      if (!foundCondition) {
        deleteAlarmOverride.push({
          alarm_rule_action_id: overrideAction.alarm_rule_action_id,
          device_id: this._deviceId,
          alarm_rule_id: this._alarmRuleId,
        });
      }
    });

    return deleteAlarmOverride;
  }

  private _findOverrideAlarmRuleConditionDto(
    alarmRule: AlarmRuleDto,
    newConditions: AlarmRuleConditionFormDto[],
  ): DeviceAlarmRuleOverrideAlarmConditionEntity[] {
    let overrides: DeviceAlarmRuleOverrideAlarmConditionEntity[] = [];
    newConditions.forEach((newCondition) => {
      let conditionClass: BaseRuleCondition = this.conditionDefinitions[newCondition.type];
      let config = conditionClass?.formToConfig(newCondition) ?? {};

      let originalCondition: AlarmRuleConditionDto | undefined = alarmRule.conditions
        ?.find((condition) => condition.id === newCondition.id);

      if (!originalCondition) return;

      let differenceToCurrentConfig: boolean = conditionClass?.isConfigDifference(
        originalCondition.config as Record<string, unknown>,
        config,
      ) ?? false;

      if (!differenceToCurrentConfig) return;

      overrides.push({
        device_id: this._deviceId,
        alarm_rule_id: this._alarmRuleId,
        alarm_rule_condition_id: originalCondition.id!,
        config: config,
      });
    });

    return overrides;
  }


  private _findOverrideAlarmRuleActionDto(
    alarmRule: AlarmRuleDto,
    newActions: AlarmRuleActionFormDto[],
  ): DeviceAlarmRuleOverrideAlarmActionEntity[] {

    let overrides: DeviceAlarmRuleOverrideAlarmActionEntity[] = [];
    newActions.forEach((newCondition) => {
      let actionClass: BaseRuleAction = this.actionDefinitions[newCondition.type];
      let config = actionClass?.formToConfig(newCondition) ?? {};

      let originalAction: AlarmRuleActionDto | undefined = alarmRule.actions
        ?.find((action) => action.id === newCondition.id);

      if (!originalAction) return;

      let differenceToCurrentConfig: boolean = actionClass?.isConfigDifference(
        originalAction.config as Record<string, unknown>,
        config,
      ) ?? false;

      if (!differenceToCurrentConfig) return;

      overrides.push({
        device_id: this._deviceId,
        alarm_rule_id: this._alarmRuleId,
        alarm_rule_action_id: originalAction.id!,
        config: config,
      });
    });

    return overrides;
  }

  private _fetchDeviceAlarmRule(): void {
    if (!this._alarmRuleId || !this._deviceId) return;

    this.loading = true;

    this._deviceService.getDeviceAlarmRuleById(this._deviceId, this._alarmRuleId)
      .subscribe({
        next: (result: BaseResponseDto<AlarmRuleDto>) => {
          this.alarmRule = result.data;

          this._fillForm();
        },
        error: (error) => {
          console.error('Error fetching alarm rule:', error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  private _fillForm(): void {
    if (!this.alarmRule) return;

    const deviceMapping: AlarmRuleDeviceDto | undefined = this.alarmRule?.devices?.find(
      (d) => d.device_id === this._deviceId);

    this.triggersFormGroup.clear();
    for (const trigger of this.alarmRule.triggers ?? []) {
      this.triggersFormGroup.push(this.triggerDefinitions[trigger.type].createFormGroup(trigger));
    }

    this.conditionsFormGroup.clear();
    this.alarmRule.conditions?.sort((a, b) => a.sort_index - b.sort_index);
    for (const condition of this.alarmRule.conditions ?? []) {
      // Find for override
      let override: DeviceAlarmRuleOverrideAlarmConditionEntity | undefined = deviceMapping?.overrideConditions
        ?.find((o) => o.alarm_rule_condition_id === condition.id);
      if (override) {
        condition.overrideConfig = override.config;
      }

      this.conditionsFormGroup.push(this.conditionDefinitions[condition.type]?.createFormGroup(condition) ??
        new FormGroup({
          id: new FormControl<number | null>(condition.id ?? null),
          type: new FormControl(condition.type, Validators.required),
        })
      );
    }

    this.actionsFormGroup.clear();
    for (const action of this.alarmRule.actions ?? []) {
      // Find for override
      let override: DeviceAlarmRuleOverrideAlarmActionEntity | undefined = deviceMapping?.overrideActions
        ?.find((o) => o.alarm_rule_action_id === action.id);
      if (override) {
        action.overrideConfig = override.config;
      }

      this.actionsFormGroup.push(this.actionDefinitions[action.type]?.createFormGroup(action) ??
        new FormGroup({
          id: new FormControl<number | null>(action.id ?? null),
          type: new FormControl(action.type, Validators.required),
        })
      );
    }

    this.formGroup.patchValue({
      name: this.alarmRule.name,
      isActive: this.alarmRule.is_active,
      description: this.alarmRule.description,
      severity: this.alarmRule.severity,
      alarmType: this.alarmRule.alarm_type,
      cooldown: this.alarmRule.cooldown_sec,
    }, {emitEvent: false});

    this.formGroup.disable({emitEvent: false});
    this.triggersFormGroup.enable({emitEvent: false});
    this.conditionsFormGroup.enable({emitEvent: false});
    this.actionsFormGroup.enable({emitEvent: false});
  }
}
